import Modal from 'components/Luxkit/Modal/Modal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { useContext, useState } from 'react'
import Spacing from 'components/Common/Spacing'
import { mediaQueryUp } from 'components/utils/breakpoint'
import rem from 'polished/lib/helpers/rem'
import styled from 'styled-components'
import Label from 'components/Luxkit/Label'
import TextLink from 'components/Luxkit/TextLink'
import ReserveForZeroPolicyModalContent from './ReserveForZeroPolicyModalContent'
import switchFunc from 'lib/switchFunc'
import { getDepositAmountI18NKeyDetails } from 'lib/TourV2/depositAmountStringBuilder'
import DepositInfoModalContent from '../DepositInfo/DepositInfoModalContent'

import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { isOfferDefaultDepositAmountPercentage } from 'lib/depositsUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { useTranslation } from 'react-i18next'
import CurrencyContext from 'contexts/currencyContext'

enum ModalSteps {
  deposit = 'deposit',
  reserve = 'reserve'
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.sm} {
    flex-direction: row;
  }
`

const Section = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(20)};

  &:nth-child(2) {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.sm} {
    width: 50%;

    &:nth-child(2) {
      margin-top: 0;
      margin-left: ${rem(20)};
    }
  }
`

const SectionSeparator = styled(Spacing)`
  display: flex;
  align-items: center;
  justify-content: center;
`

const TopSection = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
`

const BottomSection = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  margin-top: ${rem(16)};
`

interface Props {
  offerType: App.OfferType;
  depositAmountPercentage: number;
  depositType?: Tours.V2OfferDepositType
  depositThresholds?: App.DepositThreshold
  depositAmount?: number;
}

function ReserveForZeroAndDepositPolicyModal({
  depositAmountPercentage,
  depositType,
  depositThresholds,
  offerType,
  depositAmount,
}: Props) {
  const [step, setStep] = useState<ModalSteps>()

  const { t } = useTranslation(['common', 'deposit'])

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const currentCurrency = useContext(CurrencyContext)

  const depositAmountKeyDetails: ReturnType<typeof getDepositAmountI18NKeyDetails> =
    isOfferUsingDefaultDepositPercentage ? { type: 'unspecified', options: {} } :
        !depositType ? { type: 'percentage', options: { amount: depositAmountPercentage } } :
          getDepositAmountI18NKeyDetails(currentCurrency, defaultDepositAmountPercentage, depositType, depositAmountPercentage)

  const title = switchFunc({
    deposit: t('payDepositHeading', { ns: 'deposit', context: depositAmountKeyDetails.type, ...depositAmountKeyDetails.options }),
    reserve: t('Reserve now for free', { ns: 'deposit' }),
  }, t('Flexible payment options', { ns: 'deposit' }))(step as string)

  const subtitle = switchFunc({
    deposit: undefined,
    reserve: undefined,
  }, t('flexiblePaymentModalSubtitle', { ns: 'deposit' }))(step as string)

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      size="large"
      onBack={step ? () => setStep(undefined) : undefined}
    >
      {step === ModalSteps.reserve && (
        <ReserveForZeroPolicyModalContent />
      )}
      {step === ModalSteps.deposit && (
        <DepositInfoModalContent
          offerType={offerType}
          depositAmountPercentage={depositAmountPercentage}
          depositThresholds={depositThresholds}
          depositAmount={depositAmount}
        />
      )}
      {!step && (
        <SectionContainer>
          <Section>
            <TopSection>
              <Heading variant="heading6">
                {t('Reserve now for free', { ns: 'deposit' })}
              </Heading>
              <BodyText variant="medium">{t('holdReservation48Hours', { ns: 'deposit' })}</BodyText>
              <div>
                <Label kind="tertiary" variant="warning">{t('freeReservationHoldLimit', { ns: 'deposit' })}</Label>
              </div>
            </TopSection>
            <BottomSection>
              <TextLink onClick={() => setStep(ModalSteps.reserve)} size="large">{t('Learn more', { ns: 'common' })}</TextLink>
            </BottomSection>
          </Section>
          <SectionSeparator p={20}>
            <Heading variant="heading6">{t('Or', { ns: 'common' })}</Heading>
          </SectionSeparator>
          <Section>
            <TopSection>
              <Heading variant="heading6">
                {t('payDepositOptionsHeading', { ns: 'deposit', context: depositAmountKeyDetails.type, ...depositAmountKeyDetails.options })}
              </Heading>
              <BodyText variant="medium">{t('payDepositIntro', { ns: 'deposit', context: depositAmountKeyDetails.type, ...depositAmountKeyDetails.options })}</BodyText>
            </TopSection>
            <BottomSection>
              <TextLink onClick={() => setStep(ModalSteps.deposit)} size="large">{t('Learn more', { ns: 'common' })}</TextLink>
            </BottomSection>
          </Section>
        </SectionContainer>
      )}
    </Modal>
  )
}

export default ReserveForZeroAndDepositPolicyModal
